import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

import { logout } from '../../service/auth'
import { apiError, closeAccount, getCloseOptions } from '../../api/account'

import styles from './AccountSettings.module.css'

// A small area at the bottom of the page. They choose what happens to their recipes
// (kept for the family by default) and who looks after the groups they own.
const DeleteSection = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState(null)
  const [keep, setKeep] = useState(true)
  const [owners, setOwners] = useState({})
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open || options) return
    getCloseOptions()
      .then(({ data }) => {
        setOptions(data)
        const firsts = {}
        data.owned_groups.forEach((g) => {
          if (g.members[0]) firsts[g.id] = String(g.members[0].id)
        })
        setOwners(firsts)
      })
      .catch((err) => setError(apiError(err, 'Please refresh the page and try again.')))
  }, [open, options])

  const close = () => {
    setBusy(true)
    setError('')
    const new_owners = Object.fromEntries(Object.entries(owners).map(([g, u]) => [g, Number(u)]))
    closeAccount({ confirm, keep_recipes: keep, new_owners })
      .then(() => logout(navigate)(dispatch))
      .catch((err) => {
        setError(
          apiError(err, 'We could not delete your account. Please email info@getmorsel.com.')
        )
        setBusy(false)
      })
  }

  return (
    <section id="delete" className={styles.deleteArea} aria-labelledby="delete-title">
      <h2 id="delete-title" className={styles.h3} style={{ marginTop: 0 }}>
        Delete account
      </h2>
      <p className={styles.small}>
        Closes your account and erases your email, phone, photo and password. Any subscription is
        cancelled. You choose what happens to your recipes and groups first.
      </p>
      {!open ? (
        <button className={styles.btnLinkDanger} onClick={() => setOpen(true)}>
          Delete my account
        </button>
      ) : (
        <div className={styles.alert}>
          {!options ? (
            <p className={styles.small}>{error || 'Loading...'}</p>
          ) : (
            <>
              {options.recipe_count ? (
                <>
                  <b>
                    Your {options.recipe_count} {options.recipe_count === 1 ? 'recipe' : 'recipes'}
                  </b>
                  <div className={styles.list} style={{ margin: '10px 0 16px' }}>
                    <label className={styles.check}>
                      <input
                        type="radio"
                        name="close-keep"
                        checked={keep}
                        onChange={() => setKeep(true)}
                      />
                      <span>
                        <b>Keep them for my family.</b> They stay in your groups, still credited to
                        you.
                      </span>
                    </label>
                    <label className={styles.check}>
                      <input
                        type="radio"
                        name="close-keep"
                        checked={!keep}
                        onChange={() => setKeep(false)}
                      />
                      <span>
                        <b>Delete them too.</b> They disappear from every group, with their photos
                        and comments.
                      </span>
                    </label>
                  </div>
                </>
              ) : null}

              {options.owned_groups.map((g) =>
                g.members.length ? (
                  <label key={g.id} className={styles.label} style={{ marginBottom: 14 }}>
                    Who looks after {g.name} after you?
                    <select
                      className={styles.input}
                      value={owners[g.id] || ''}
                      onChange={(e) => setOwners((o) => ({ ...o, [g.id]: e.target.value }))}
                    >
                      {g.members.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.fullname}
                          {m.joined_at ? ` (member since ${moment(m.joined_at).year()})` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <p key={g.id} className={styles.small}>
                    You are the only member of {g.name}, so it will be removed.
                  </p>
                )
              )}

              {options.shared_count ? (
                <p className={styles.small}>
                  The {options.shared_count} {options.shared_count === 1 ? 'person' : 'people'} on
                  your Family Pro plan will go back to the free plan.
                </p>
              ) : null}

              <label className={styles.label}>
                This can't be undone. Type DELETE to confirm.
                <input
                  className={styles.input}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="off"
                  style={{ maxWidth: 240 }}
                />
              </label>
              <div className={styles.row} style={{ marginTop: 14 }}>
                <button
                  className={styles.btnDanger}
                  disabled={busy || confirm.trim().toUpperCase() !== 'DELETE'}
                  onClick={close}
                >
                  {busy ? 'Deleting...' : 'Delete forever'}
                </button>
                <button className={styles.btn} onClick={() => setOpen(false)}>
                  Keep my account
                </button>
              </div>
              {error ? <p className={styles.error}>{error}</p> : null}
            </>
          )}
        </div>
      )}
    </section>
  )
}

export default DeleteSection
